/* Client-side CSV exports of the fitness data (Profile → Backup). */
import { entryAmount, entryMacros } from './food';
import { MEASUREMENT_SITES, cleanMeasurements } from './logic/measurements';
import { dayFor } from './session';

const isObj = v => v !== null && typeof v === 'object' && !Array.isArray(v);

function cell(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(header, rows) {
  return [header, ...rows].map(r => r.map(cell).join(',')).join('\n') + '\n';
}

const round = (v, n = 1) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 10 ** n) / 10 ** n : '');

export function workoutsCsv(state) {
  const rows = [];
  for (const date of Object.keys(isObj(state.logs) ? state.logs : {}).sort()) {
    const day = state.logs[date];
    if (!isObj(day)) continue;
    const planDay = dayFor(state, date)?.day?.name || '';
    for (const [exercise, sets] of Object.entries(day)) {
      (Array.isArray(sets) ? sets : []).forEach((s, i) => {
        if (!isObj(s) || (s.weight == null && s.reps == null)) return;
        rows.push([date, planDay, exercise, i + 1, s.weight ?? '', s.reps ?? '', typeof s.rir === 'number' ? s.rir : '', s.done ? 1 : 0]);
      });
    }
  }
  return toCsv(['date', 'plan_day', 'exercise', 'set', 'weight_kg', 'reps', 'rir', 'done'], rows);
}

export function nutritionCsv(state) {
  const rows = [];
  for (const date of Object.keys(isObj(state.nutrition) ? state.nutrition : {}).sort()) {
    for (const e of Array.isArray(state.nutrition[date]) ? state.nutrition[date] : []) {
      if (!isObj(e)) continue;
      const m = entryMacros(e);
      rows.push([date, e.group || 'snack', e.name || '', round(entryAmount(e), 2), e.unit || 'serving',
        Math.round(m.kcal), round(m.protein), round(m.carbs), round(m.fats)]);
    }
  }
  return toCsv(['date', 'group', 'name', 'amount', 'unit', 'kcal', 'protein_g', 'carbs_g', 'fats_g'], rows);
}

export function weightsCsv(state) {
  const rows = (Array.isArray(state.bodyMetrics) ? state.bodyMetrics : [])
    .filter(m => isObj(m) && m.date && typeof m.weight === 'number')
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(m => [m.date, m.weight]);
  return toCsv(['date', 'weight_kg'], rows);
}

export function measurementsCsv(state) {
  const rows = cleanMeasurements(state.measurements).map(m => [m.date, ...MEASUREMENT_SITES.map(s => m[s.id] ?? ''), m.note || '']);
  return toCsv(['date', ...MEASUREMENT_SITES.map(s => `${s.id}_cm`), 'note'], rows);
}

export const CSV_EXPORTS = [
  { id: 'workouts', label: 'Workouts', build: workoutsCsv },
  { id: 'nutrition', label: 'Nutrition', build: nutritionCsv },
  { id: 'weights', label: 'Weights', build: weightsCsv },
  { id: 'measurements', label: 'Measurements', build: measurementsCsv },
];

export function downloadText(filename, text, type) {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/* The JSON export: the synced data only, never device-local fields. */
export function exportableState(state) {
  const { progressPhotos, restTimer, ...rest } = state;
  if (isObj(rest.reminders)) rest.reminders = { ...rest.reminders, lastSentDate: undefined };
  return rest;
}
